import { useEffect, useMemo, useState } from 'react';
import {
  ActionableNotification,
  Modal,
  RadioButton,
  RadioButtonGroup,
  TextArea,
  TextInput,
  InlineNotification,
  InlineLoading,
  Tag,
} from '@carbon/react';
import { usePublishVersion, useVersions } from '../../lib/query/hooks/useVersions';
import { outcomeMessage } from '../../lib/query/useResilientMutation';

interface PublishVersionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  ontologyId: string | undefined;
  onPublished?: (publishedVersion: string) => void;
}

type BumpKind = 'patch' | 'minor' | 'major' | 'custom';

/**
 * The number the very first publish receives. Mirrors FIRST_PUBLISHED_VERSION in
 * the backend's version.repository: the server decides, this is only the preview.
 */
const FIRST_PUBLISHED_VERSION = '0.1.0';

/**
 * Parses a semver-like string ("1.2.3") and returns [major, minor, patch].
 * Missing components count as 0 so a legacy "1.0" previews as 1.0.1 on patch.
 */
function parseSemver(v: string): [number, number, number] {
  const parts = v.split('.').map((p) => parseInt(p, 10));
  return [parts[0] || 0, parts[1] || 0, parts[2] || 0];
}

/**
 * Preview of the number this publish will produce.
 *
 * `latestPublished` is null until the ontology has been published once; there is
 * nothing to bump, so every bump kind previews the same first number. The
 * baseline is the latest PUBLISHED version, never the draft: a draft has no
 * number (migration 043).
 */
function previewBump(latestPublished: string | null, bump: BumpKind): string {
  if (!latestPublished) return FIRST_PUBLISHED_VERSION;
  const [maj, min, pat] = parseSemver(latestPublished);
  switch (bump) {
    case 'major':
      return `${maj + 1}.0.0`;
    case 'minor':
      return `${maj}.${min + 1}.0`;
    default:
      return `${maj}.${min}.${pat + 1}`;
  }
}

/**
 * PublishVersionDialog
 *
 * Step C — Publish + Fork. Lets the owner snapshot the current draft as
 * an immutable published version, automatically forking a brand-new draft
 * that becomes the working copy.
 */
export const PublishVersionDialog = ({
  isOpen,
  onClose,
  ontologyId,
  onPublished,
}: PublishVersionDialogProps) => {
  const [bump, setBump] = useState<BumpKind>('patch');
  const [customVersion, setCustomVersion] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const versionsQuery = useVersions(isOpen ? ontologyId : undefined);
  const publishMutation = usePublishVersion(ontologyId);

  // Newest published first: version_number is a string, so sorting on it would
  // put "0.10.0" before "0.9.0". Sort on time, like the server does.
  const publishedVersions = useMemo(
    () =>
      (versionsQuery.data ?? [])
        .filter((v) => v.status === 'published')
        .sort(
          (a, b) =>
            new Date(b.published_at ?? b.created_at).getTime() -
            new Date(a.published_at ?? a.created_at).getTime()
        ),
    [versionsQuery.data]
  );
  const latestPublished = publishedVersions[0]?.version_number ?? null;
  const isFirstPublish = latestPublished === null;

  // Reset state every time the dialog opens, or when the baseline arrives (the
  // version list is fetched, so it is null on the first render).
  useEffect(() => {
    if (isOpen) {
      setBump('patch');
      setCustomVersion(previewBump(latestPublished, 'patch'));
      setReleaseNotes('');
      setError(null);
      publishMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, latestPublished]);

  const previewVersion = useMemo(() => {
    if (bump === 'custom') return customVersion.trim();
    return previewBump(latestPublished, bump);
  }, [bump, customVersion, latestPublished]);

  const isPublishing = publishMutation.isPending;
  const publishFailure = outcomeMessage(publishMutation.error);
  const customInvalid =
    bump === 'custom' && !/^\d+\.\d+\.\d+$/.test(customVersion.trim());

  async function handleSubmit() {
    if (!ontologyId) return;
    if (customInvalid) {
      setError('Custom version must follow semver: MAJOR.MINOR.PATCH');
      return;
    }

    setError(null);
    // Publish is the least repeatable mutation in the app: a second attempt burns a second
    // number and forks a second draft. `submit` reads the server truth before letting the user
    // believe a lost response was a failure.
    const result = await publishMutation.submit({
      ...(bump === 'custom' ? { version: customVersion.trim() } : { bump }),
      release_notes: releaseNotes.trim() ? releaseNotes.trim() : null,
    });
    if (result) {
      onPublished?.(result.published.version_number);
      onClose();
    }
  }

  return (
    <Modal
      open={isOpen}
      modalHeading="Publish version"
      modalLabel="Versioning"
      primaryButtonText={isPublishing ? 'Publishing…' : 'Publish'}
      secondaryButtonText="Cancel"
      primaryButtonDisabled={isPublishing || customInvalid || !ontologyId}
      onRequestClose={() => {
        if (!isPublishing) onClose();
      }}
      onRequestSubmit={handleSubmit}
      size="sm"
    >
      <p style={{ marginBottom: '1rem' }}>
        Publishing gives the current draft its version number and freezes it. A
        new draft is forked so you can keep editing; that draft stays unnumbered
        until you publish it in turn.
      </p>

      {isFirstPublish && (
        <p style={{ marginBottom: '1rem' }}>
          This is the first publish, so the bump has nothing to apply to: the
          version will be {FIRST_PUBLISHED_VERSION}. Pick Custom to choose
          another one.
        </p>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {/* No "current draft: vX" tag: a draft has no number to show. */}
        {isFirstPublish ? (
          <Tag type="cyan">Never published</Tag>
        ) : (
          <Tag type="green">Last published: v{latestPublished}</Tag>
        )}
      </div>

      <RadioButtonGroup
        legendText="Version bump"
        name="version-bump"
        valueSelected={bump}
        onChange={(value) => {
          const next = value as BumpKind;
          setBump(next);
          if (next !== 'custom') {
            setCustomVersion(previewBump(latestPublished, next));
          }
        }}
        orientation="vertical"
      >
        <RadioButton
          id="bump-patch"
          labelText={`Patch: ${previewBump(latestPublished, 'patch')}`}
          value="patch"
        />
        <RadioButton
          id="bump-minor"
          labelText={`Minor: ${previewBump(latestPublished, 'minor')}`}
          value="minor"
        />
        <RadioButton
          id="bump-major"
          labelText={`Major: ${previewBump(latestPublished, 'major')}`}
          value="major"
        />
        <RadioButton id="bump-custom" labelText="Custom…" value="custom" />
      </RadioButtonGroup>

      {bump === 'custom' && (
        <div style={{ marginTop: '0.75rem' }}>
          <TextInput
            id="custom-version"
            labelText="Custom version (semver)"
            placeholder="e.g. 2.0.0"
            value={customVersion}
            invalid={customInvalid && customVersion.length > 0}
            invalidText="Must follow MAJOR.MINOR.PATCH"
            onChange={(e) => setCustomVersion(e.target.value)}
          />
        </div>
      )}

      <div style={{ marginTop: '1rem' }}>
        <TextArea
          id="release-notes"
          labelText="Release notes (optional)"
          placeholder="What changed in this version?"
          value={releaseNotes}
          rows={3}
          onChange={(e) => setReleaseNotes(e.target.value)}
        />
      </div>

      <p style={{ marginTop: '1rem' }}>
        About to publish as <strong>{previewVersion || '—'}</strong>.
      </p>

      {error && (
        <InlineNotification
          kind="error"
          title="Publish failed"
          subtitle={error}
          onCloseButtonClick={() => setError(null)}
          style={{ marginTop: '0.75rem' }}
        />
      )}

      {publishFailure && (
        <ActionableNotification
          kind={publishMutation.isUnconfirmed ? 'warning' : 'error'}
          title={publishMutation.isUnconfirmed ? 'Not confirmed' : 'Publish failed'}
          subtitle={publishFailure}
          lowContrast
          hideCloseButton
          inline={false}
          actionButtonLabel="Retry"
          onActionButtonClick={() => void publishMutation.retry()}
          style={{ marginTop: '0.75rem' }}
        />
      )}

      {isPublishing && (
        <div style={{ marginTop: '0.75rem' }}>
          <InlineLoading
            description={
              publishMutation.isVerifying
                ? 'Connection problem. Checking with the server whether the version was published…'
                : 'Publishing version…'
            }
          />
        </div>
      )}
    </Modal>
  );
};

// Made with Bob
